import {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Flex, HStack, Spinner, Text} from '@chakra-ui/react';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  SearchSelectionInput,
  ViewportAwareDropdownMenu,
  handleDropdownInputKeyDown,
  handleDropdownOptionKeyDown,
} from '../planner/CreateSessionFormSections';
import type {CareTeamMember} from '../../types/telephoneEncounter';

// Searchable select over a roster of people. Used for both the encounter's
// provider and its assignee. Built on the same typeahead primitives as the New
// Encounter form so it reads as the same form system — important because the
// EHR-synced provider/resource roster can be large.
// Default debounce for the optional server-side search: the roster lookup
// only fires after the user pauses typing for this long, so a fast typist
// doesn't stampede the backend with one request per keystroke.
const DEFAULT_SEARCH_DEBOUNCE_MS = 1000;

const CareTeamSelect = ({
  value,
  members,
  onChange,
  placeholder = 'Select…',
  isDisabled = false,
  allowClear = false,
  clearLabel = 'Unassigned',
  ariaLabel = 'Select a team member',
  onSearch,
  isSearching = false,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
}: {
  value: string | null;
  members: CareTeamMember[];
  onChange: (member: CareTeamMember | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  allowClear?: boolean;
  clearLabel?: string;
  ariaLabel?: string;
  // Optional debounced server-side search. When provided, the typed query is
  // forwarded to the parent after `searchDebounceMs` of inactivity so it can
  // fetch additional matches from the backend. Local filtering over `members`
  // still happens immediately for instant feedback; this just widens the set
  // beyond whatever was preloaded. Omit it for purely local pickers.
  onSearch?: (query: string) => void;
  // When true, a small "Searching…" hint renders in the dropdown so the user
  // knows a server lookup is in flight.
  isSearching?: boolean;
  searchDebounceMs?: number;
}) => {
  const {
    borderColor,
    subtleText,
    surfaceBg,
    textColor,
    typeaheadHoverBg,
    typeaheadSelectedBg,
    typeaheadSelectedBorder,
  } = usePlannerStyles();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => members.find(member => member.id === value) || null,
    [members, value],
  );

  const filtered = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    if (!queryText) return members;
    return members.filter(
      member =>
        member.name.toLowerCase().includes(queryText) ||
        (member.role || '').toLowerCase().includes(queryText),
    );
  }, [members, search]);

  const displayValue = isOpen ? search : selected?.name || '';
  const listboxId = useMemo(
    () => `te-careteam-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );
  const inputSelector = `input[aria-label="${ariaLabel}"]`;

  // Debounced server-side search. We keep the callback in a ref so the
  // effect can depend only on the query text + open state, and we track the
  // last query we actually dispatched so flipping back to an already-searched
  // string (or reopening) doesn't re-fire the request.
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);
  const lastDispatchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onSearch || isDisabled || !isOpen) return undefined;
    const trimmed = search.trim();
    // Empty query → the local list already shows everyone; no server hit.
    if (!trimmed) return undefined;
    if (trimmed === lastDispatchedRef.current) return undefined;
    // Wait for the typing to settle (default 1s) before searching, and reset
    // the timer on every keystroke so only the final pause triggers a fetch.
    const timer = setTimeout(() => {
      lastDispatchedRef.current = trimmed;
      onSearchRef.current?.(trimmed);
    }, searchDebounceMs);
    return () => clearTimeout(timer);
  }, [search, isOpen, isDisabled, onSearch, searchDebounceMs]);

  const close = () => {
    setIsOpen(false);
    setSearch('');
    // Let the next open re-search from scratch.
    lastDispatchedRef.current = null;
  };

  const handleSelect = (member: CareTeamMember | null) => {
    onChange(member);
    close();
  };

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          close();
        }
      }}
    >
      <SearchSelectionInput
        ariaControls={listboxId}
        ariaLabel={ariaLabel}
        isOpen={isOpen}
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => !isDisabled && setIsOpen(true)}
        onClick={() => !isDisabled && setIsOpen(true)}
        onChange={(event: any) => {
          if (isDisabled) return;
          setSearch(event.target.value);
          setIsOpen(true);
        }}
        onKeyDown={(event: any) =>
          handleDropdownInputKeyDown({
            event,
            menuId: listboxId,
            onOpen: () => !isDisabled && setIsOpen(true),
          })
        }
      />

      {isOpen && !isDisabled && (
        <ViewportAwareDropdownMenu
          anchorRef={anchorRef}
          id={listboxId}
          className="menu"
          zIndex={1400}
          bg={surfaceBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="lg"
          maxHeightPx={280}
          role="listbox"
          aria-label={ariaLabel}
          p={2}
        >
          {allowClear ? (
            <Button
              type="button"
              className="item"
              role="option"
              aria-selected={!value}
              aria-label={clearLabel}
              variant="ghost"
              justifyContent="flex-start"
              w="100%"
              h="auto"
              minH="44px"
              py={2.5}
              px={3}
              borderRadius="lg"
              bg="transparent"
              borderWidth="1px"
              borderColor="transparent"
              onMouseDown={event => event.preventDefault()}
              onClick={() => handleSelect(null)}
              onKeyDown={event =>
                handleDropdownOptionKeyDown(event, inputSelector)
              }
              _hover={{bg: typeaheadHoverBg}}
              _focus={{
                bg: typeaheadHoverBg,
                boxShadow: 'none',
                outline: 'none',
              }}
              _focusVisible={{
                bg: typeaheadHoverBg,
                boxShadow: 'none',
                outline: 'none',
              }}
            >
              <Text color={subtleText} fontSize="md" noOfLines={1}>
                {clearLabel}
              </Text>
            </Button>
          ) : null}

          {filtered.length > 0 ? (
            filtered.map(member => {
              const isSelected = member.id === value;
              return (
                <Button
                  key={member.id}
                  type="button"
                  className="item"
                  role="option"
                  aria-selected={isSelected}
                  aria-label={`Select ${member.name}`}
                  variant="ghost"
                  justifyContent="flex-start"
                  w="100%"
                  h="auto"
                  minH="44px"
                  py={2.5}
                  px={3}
                  borderRadius="lg"
                  bg={isSelected ? typeaheadSelectedBg : 'transparent'}
                  borderWidth="1px"
                  borderColor={
                    isSelected ? typeaheadSelectedBorder : 'transparent'
                  }
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => handleSelect(member)}
                  onKeyDown={event =>
                    handleDropdownOptionKeyDown(event, inputSelector)
                  }
                  _hover={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                  }}
                  _focus={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                  _focusVisible={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                >
                  <Flex align="baseline" gap={2} minW={0}>
                    <Text color={textColor} fontSize="md" noOfLines={1}>
                      {member.name}
                    </Text>
                    {member.role ? (
                      <Text
                        fontSize="sm"
                        color={subtleText}
                        flexShrink={0}
                        noOfLines={1}
                      >
                        {member.role}
                      </Text>
                    ) : null}
                  </Flex>
                </Button>
              );
            })
          ) : isSearching ? null : (
            <Text color={subtleText} fontSize="sm" px={3} py={3}>
              No matches.
            </Text>
          )}

          {isSearching ? (
            <HStack spacing={2} px={3} py={2.5} color={subtleText}>
              <Spinner size="xs" />
              <Text fontSize="sm">Searching…</Text>
            </HStack>
          ) : null}
        </ViewportAwareDropdownMenu>
      )}
    </Box>
  );
};

export default CareTeamSelect;
